import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import axios from "axios";
import { useLang } from "../i18n";
import { API } from "../config";
import { Star } from "lucide-react";
import { FaGoogle, FaTripadvisor } from "react-icons/fa";

export default function Reviews() {
  const { t } = useLang();
  const [live, setLive] = useState(null);

  useEffect(() => {
    axios.get(`${API}/reviews`)
      .then(({ data }) => {
        if (data.configured && data.reviews?.length) setLive(data);
      })
      .catch(() => {});
  }, []);

  const items = live
    ? live.reviews.map((r) => ({ n: r.author || "Google", c: "Google", src: "Google", txt: r.txt || r.text, rating: r.rating }))
    : t.reviews.items;

  return (
    <section id="reviews" className="max-w-7xl mx-auto px-6 py-24">
      <div className="flex flex-wrap items-end justify-between gap-6 mb-12">
        <div>
          <p className="eyebrow mb-4">{t.reviews.eyebrow}</p>
          <h2 className="font-display text-2xl sm:text-3xl lg:text-4xl tracking-tight font-bold text-topacio-ink">{t.reviews.title}</h2>
        </div>
        {live ? (
          <a href={live.maps_url} target="_blank" rel="noopener noreferrer" data-testid="google-rating-badge"
            className="card-brutal flex items-center gap-3 px-5 py-3 hover:-translate-y-0.5 hover:shadow-[6px_6px_0_0_#111] transition-[transform,box-shadow]">
            <FaGoogle className="w-5 h-5 text-topacio-faded" />
            <span className="font-display font-black text-xl text-topacio-ink">{live.rating}</span>
            <span className="flex gap-0.5">
              {[...Array(5)].map((_, s) => (
                <Star key={s} className={`w-3.5 h-3.5 ${s < Math.round(live.rating) ? "fill-brand text-topacio-ink" : "text-zinc-300"}`} strokeWidth={1.5} />
              ))}
            </span>
            <span className="text-xs text-topacio-faded font-bold">{live.total} · Google</span>
          </a>
        ) : (
          <p className="text-topacio-faded text-sm font-semibold flex items-center gap-3">
            <FaGoogle className="w-4 h-4" /> <FaTripadvisor className="w-5 h-5" /> {t.reviews.sub}
          </p>
        )}
      </div>
      <div className="mb-8">
        <a href="https://www.kayak.es" target="_blank" rel="noopener noreferrer" data-testid="kayak-badge"
          className="inline-flex items-center gap-2.5 border-2 border-topacio-ink rounded-lg bg-white px-4 py-2.5 shadow-[3px_3px_0_0_#111] hover:-translate-y-0.5 hover:shadow-[5px_5px_0_0_#111] transition-[transform,box-shadow]">
          <span className="font-display font-black text-sm text-[#FF690F]">KAYAK</span>
          <span className="text-xs font-bold text-topacio-faded">{t.reviews.kayak}</span>
        </a>
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
        {items.slice(0, 4).map((r, i) => (
          <motion.div key={i} data-testid={`review-card-${i}`}
            initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }} transition={{ duration: 0.5, delay: i * 0.08 }}
            className={`card-brutal p-7 flex flex-col hover:-translate-y-1 hover:shadow-[8px_8px_0_0_#111] transition-[transform,box-shadow] ${i % 2 ? "rotate-1" : "-rotate-1"} hover:rotate-0`}>
            <div className="flex items-center justify-between">
              <div className="flex gap-0.5">
                {[...Array(5)].map((_, s) => (
                  <Star key={s} className={`w-4 h-4 ${s < (r.rating || 5) ? "fill-brand text-topacio-ink" : "text-zinc-300"}`} strokeWidth={1.5} />
                ))}
              </div>
              {r.src === "Google" ? <FaGoogle className="w-4 h-4 text-topacio-faded" /> : <FaTripadvisor className="w-5 h-5 text-topacio-faded" />}
            </div>
            <p className="text-sm text-topacio-ink leading-relaxed mt-5 flex-1 line-clamp-6">“{r.txt}”</p>
            <div className="mt-6 pt-4 border-t-2 border-dashed border-zinc-200">
              <div className="font-extrabold text-topacio-ink text-sm">{r.n}</div>
              <div className="text-xs text-topacio-faded mt-0.5">{r.c}</div>
            </div>
          </motion.div>
        ))}
      </div>
      {live && (
        <p className="mt-6 text-[11px] text-topacio-faded font-semibold">
          Reseñas de Google Maps, ordenadas por relevancia. Google no verifica necesariamente las reseñas.
        </p>
      )}
    </section>
  );
}
